// ============================================================
// CODIVUS PLATFORM — Mock data store
// ============================================================

export interface Org {
  id: string; name: string; slug: string; plan: string; avatar: string;
}

export interface Project {
  id: string; orgId: string; name: string; description: string;
  status: 'active' | 'idle' | 'archived'; emoji: string;
  sessions: number; lastActive: string; createdAt: string;
  members: string[]; tags: string[]; apiCalls: number;
}

export interface Session {
  id: string; projectId: string; projectName: string;
  name: string; status: 'running' | 'idle' | 'completed' | 'error';
  model: string; duration: string; tokens: number;
  startedAt: string; endedAt: string | null; user: string;
  userEmail: string;
}

export interface User {
  id: string; orgId: string; name: string; email: string;
  role: 'owner' | 'admin' | 'developer' | 'viewer';
  status: 'active' | 'invited' | 'suspended';
  avatar: string; joinedAt: string; lastActive: string;
}

export interface ApiToken {
  id: string; name: string; prefix: string; secret: string;
  scopes: string[]; createdAt: string; lastUsed: string | null;
  expiresAt: string | null; status: 'active' | 'revoked';
  createdBy: string;
  orgId?: string | null;
}

export interface Webhook {
  id: string; url: string; events: string[]; status: 'active' | 'inactive';
  secret: string; createdAt: string; lastTriggered: string | null;
  successRate: number; totalDeliveries: number;
  orgId?: string | null;
}

export interface Invoice {
  id: string; date: string; description: string;
  amount: number; status: 'paid' | 'pending' | 'failed';
  downloadUrl: string;
  orgId?: string | null;
}

export interface AuditEntry {
  id: string; timestamp: string; userId: string; userName: string;
  action: string; resource: string; resourceId: string;
  ip: string; userAgent: string; result: 'success' | 'failure';
}

// ---- State ----
export interface AppState {
  authenticated: boolean;
  currentOrg: string;
  currentUser: string;
  orgs: Org[];
  projects: Project[];
  sessions: Session[];
  users: User[];
  tokens: ApiToken[];
  webhooks: Webhook[];
  invoices: Invoice[];
  auditLog: AuditEntry[];
  notifications: number;
  usage: {
    projectsCreated: number;
    sessionsCreated: number;
    tokensUsed: number;
  } | null;
  limits: {
    projectsPerMonth: number;
    sessionsPerMonth: number;
    tokensPerMonth: number;
    maxConcurrentSessions: number;
  } | null;
  plan: { id: string; name: string } | null;
  subscription: {
    id: string;
    planId: string;
    /** Provider status: 'active' | 'trialing' | 'past_due' | 'cancelled' | 'incomplete' | 'expired' */
    status: string;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
  } | null;
}

const COLORS = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#22c55e','#3b82f6','#14b8a6','#f97316'];
const colorOf = (i: number) => COLORS[i % COLORS.length];

export const state: AppState = {
  authenticated: false,
  currentOrg: '',
  currentUser: '',
  notifications: 0,

  orgs: [],
  projects: [],
  sessions: [],
  users: [],
  tokens: [],
  webhooks: [],
  invoices: [],
  auditLog: [],
  usage: null,
  limits: null,
  plan: null,
  subscription: null,
};

export function getOrg(): Org {
  return state.orgs.find(o => o.id === state.currentOrg)
    ?? { id: '', name: '—', slug: '', plan: 'Free', avatar: '—' };
}
export function getCurrentUser(): User {
  return state.users.find(u => u.id === state.currentUser)
    ?? {
      id: state.currentUser, orgId: state.currentOrg, name: '', email: '',
      role: 'viewer', status: 'active', avatar: '', joinedAt: '', lastActive: '—',
    };
}
export function getOrgProjects(): Project[] {
  return state.projects.filter(p => p.orgId === state.currentOrg);
}
export function getActiveSessions(): Session[] {
  return state.sessions.filter(s => s.status === 'running' || s.status === 'idle');
}
export function avatarColor(id: string): string {
  const idx = id.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return colorOf(idx);
}
